<!-- TimetableAddForm.vue -->
<template>
  <div class="timetable-add-form">
    <h3>{{ isEditing ? 'Edit' : 'Add New' }} Timetable Entry</h3>
    <form @submit.prevent="submitForm">
      <div class="form-row">
        <div class="form-group">
          <label>Class</label>
          <select v-model="form.class_id" @change="fetchSections">
            <option value="">Select Class</option>
            <option v-for="classItem in classes" :value="classItem.id">{{ classItem.name }}</option>
          </select>
        </div>

        <div class="form-group">
          <label>Section</label>
          <select v-model="form.section_id" :disabled="!form.class_id">
            <option value="">Select Section</option>
            <option v-for="section in sections" :value="section.id">{{ section.name }}</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Subject</label>
          <select v-model="form.subject_id">
            <option value="">Select Subject</option>
            <option v-for="subject in subjects" :value="subject.id">{{ subject.name }} ({{ subject.code }})</option>
          </select>
        </div>

        <div class="form-group">
          <label>Teacher</label>
          <select v-model="form.teacher_id">
            <option value="">Select Teacher</option>
            <option v-for="teacher in teachers" :value="teacher.id">{{ teacher.name }}</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Day of Week</label>
          <select v-model="form.day_of_week">
            <option value="sunday">Sunday</option>
            <option value="monday">Monday</option>
            <option value="tuesday">Tuesday</option>
            <option value="wednesday">Wednesday</option>
            <option value="thursday">Thursday</option>
            <option value="friday">Friday</option>
            <option value="saturday">Saturday</option>
          </select>
        </div>

        <div class="form-group">
          <label>Start Time</label>
          <input type="time" v-model="form.start_time" required>
        </div>

        <div class="form-group">
          <label>End Time</label>
          <input type="time" v-model="form.end_time" required>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group checkbox-group">
          <input type="checkbox" v-model="form.is_recurring" id="isRecurring">
          <label for="isRecurring">Recurring Weekly</label>
        </div>

        <div class="form-group" v-if="!form.is_recurring">
          <label>Specific Date</label>
          <input type="date" v-model="form.specific_date">
        </div>

        <div class="form-group checkbox-group">
          <input type="checkbox" v-model="form.is_holiday" id="isHoliday">
          <label for="isHoliday">Is Holiday</label>
        </div>
      </div>

      <div class="form-group">
        <label>Notes</label>
        <textarea v-model="form.notes" rows="2"></textarea>
      </div>

      <div class="form-actions">
        <button type="submit" :disabled="loading">
          {{ isEditing ? 'Update' : 'Save' }} Entry
        </button>
        <button type="button" @click="cancelForm">Cancel</button>
        <button type="button" @click="resetForm" v-if="!isEditing">Reset</button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';

export default {
  name: 'TimetableAddForm',
  props: {
    api: {
      type: Object,
      required: true
    },
    entry: {
      type: Object,
      default: null
    },
    isEditing: {
      type: Boolean,
      default: false
    }
  },
  emits: ['entry-added', 'cancel'],
  setup(props, { emit }) {
    const form = ref({
      id: null,
      class_id: '',
      section_id: '',
      subject_id: '',
      teacher_id: '',
      day_of_week: 'sunday',
      start_time: '',
      end_time: '',
      is_recurring: true,
      specific_date: '',
      is_holiday: false,
      notes: ''
    });

    const classes = ref([]);
    const sections = ref([]);
    const subjects = ref([]);
    const teachers = ref([]);
    const loading = ref(false);

    // Watch for changes in the entry prop
    watch(() => props.entry, (newEntry) => {
      if (newEntry) {
        populateForm(newEntry);
      }
    }, { immediate: true });

    const populateForm = (entry) => {
      if (!entry) return;
      
      form.value = {
        id: entry.id,
        class_id: entry.class_id || '',
        section_id: entry.section_id || '',
        subject_id: entry.subject_id || '',
        teacher_id: entry.teacher_id || '',
        day_of_week: entry.day_of_week || 'sunday',
        start_time: entry.start_time || '',
        end_time: entry.end_time || '',
        is_recurring: entry.is_recurring !== undefined ? entry.is_recurring : true,
        specific_date: entry.specific_date || '',
        is_holiday: entry.is_holiday || false,
        notes: entry.notes || ''
      };

      // If class is set, fetch its sections
      if (entry.class_id) {
        fetchSections();
      }
    };

    const fetchClasses = async () => {
      try {
        const response = await props.api.get('/classes');
        classes.value = response.data;
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };

    const fetchSections = async () => {
      if (!form.value.class_id) {
        sections.value = [];
        return;
      }
      try {
        const response = await props.api.get(`/classes/${form.value.class_id}/sections`);
        sections.value = response.data.sections || [];
      } catch (error) {
        console.error('Error fetching sections:', error);
      }
    };

    const fetchSubjects = async () => {
      try {
        const response = await props.api.get('/subjects');
        subjects.value = response.data.data;
      } catch (error) {
        console.error('Error fetching subjects:', error);
      }
    };

    const fetchTeachers = async () => {
      try {
        // Fetch employees where is_teacher = true
        const response = await props.api.get('/get-teachers');
        teachers.value = response.data.data;
      } catch (error) {
        console.error('Error fetching teachers:', error);
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        // Calculate duration in minutes
        const duration = calculateDuration(form.value.start_time, form.value.end_time);
        const formData = {
          ...form.value,
          duration: duration
        };

        if (props.isEditing) {
          // Update existing entry
          await props.api.put(`/timetable/${form.value.id}`, formData);
          emit('entry-added');
        } else {
          // Create new entry
          await props.api.post('/timetable', {
            entries: [formData]
          });
          resetForm();
          emit('entry-added');
        }
      } catch (error) {
        console.error('Error saving timetable entry:', error);
        alert('Failed to save timetable entry. Please try again.');
      } finally {
        loading.value = false;
      }
    };

    const calculateDuration = (startTime, endTime) => {
      if (!startTime || !endTime) return 0;
      
      const start = new Date(`2000-01-01T${startTime}`);
      const end = new Date(`2000-01-01T${endTime}`);
      
      return (end - start) / (1000 * 60); // Convert to minutes
    };

    const resetForm = () => {
      form.value = {
        class_id: '',
        section_id: '',
        subject_id: '',
        teacher_id: '',
        day_of_week: 'sunday',
        start_time: '',
        end_time: '',
        is_recurring: true,
        specific_date: '',
        is_holiday: false,
        notes: ''
      };
    };

    const cancelForm = () => {
      emit('cancel');
    };

    onMounted(() => {
      fetchClasses();
      fetchSubjects();
      fetchTeachers();
      
      // If editing, populate form with entry data
      if (props.entry) {
        populateForm(props.entry);
      }
    });

    return {
      form,
      classes,
      sections,
      subjects,
      teachers,
      loading,
      fetchSections,
      submitForm,
      resetForm,
      cancelForm,
      populateForm
    };
  }
};
</script>

<style scoped>
.timetable-add-form {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.timetable-add-form h3 {
  margin-top: 0;
  color: #2c3e50;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.form-group {
  flex: 1;
  margin-bottom: 10px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #555;
}

.form-group select,
.form-group input[type="time"],
.form-group input[type="date"],
.form-group textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 25px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.form-actions button {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.form-actions button[type="submit"] {
  background-color: #42b983;
  color: white;
  border: none;
}

.form-actions button[type="button"] {
  background-color: #f0f0f0;
  border: 1px solid #ddd;
}

.form-actions button[type="button"]:hover {
  background-color: #e0e0e0;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 10px;
  }

  .form-actions {
    flex-wrap: wrap;
  }

  .form-actions button {
    flex: 1;
    min-width: 100px;
  }
}
</style>